/**
 * 科目(docs/20 §5・docs/19 §3 SubjectDef)。
 * 一覧: ID / 表示名 / 状態 / キャラクター / 通貨 / 順序。
 * 編集: 表示名、色・アイコン、キャラクター付け替え、有効・休止、並び順。ID は作成後変えない。
 * 新規作成: ID は通貨キーと同じ規則(既存の通貨のうち、特別通貨と使用済みを除いたものから選ぶ)。
 */
import { useMemo, useState } from 'react';
import { SUBJECT_STATUSES, type CharacterDef, type SubjectDef } from '@paltyping/content-schema';
import {
  availableSubjectIds,
  fieldIssues,
  issuesFor,
  newSubjectDef,
  validateDrafts,
  type CatalogChange,
} from '../../services/catalog';
import {
  CatalogSummary,
  IssueList,
  NumberInput,
  ReadOnlyNote,
  SaveNotice,
  type CatalogStore,
  type SaveOutcome,
} from './common';

export const SUBJECT_STATUS_LABELS: Readonly<Record<SubjectDef['status'], string>> = {
  active: '有効',
  paused: '休止',
};

interface Props {
  readonly store: CatalogStore;
  readonly editable: boolean;
}

export function SubjectsPanel({ store, editable }: Props) {
  const { drafts } = store;
  const [selectedId, setSelectedId] = useState<string | null>(drafts.subject[0]?.id ?? null);
  const [creating, setCreating] = useState<SubjectDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
  const [newId, setNewId] = useState('');

  const selected = drafts.subject.find((entry) => entry.id === selectedId);
  const characters = drafts.character.map((entry) => entry.data.def);
  const available = availableSubjectIds(drafts);
  const summary = useMemo(() => validateDrafts(drafts), [drafts]);

  async function save(changes: CatalogChange[]): Promise<boolean> {
    setBusy(true);
    setOutcome(null);
    const next = await store.save(changes);
    setOutcome(next);
    setBusy(false);
    if (next.ok) setCreating(null);
    return next.ok;
  }

  function move(index: number, delta: number): void {
    const a = drafts.subject[index];
    const b = drafts.subject[index + delta];
    if (!a || !b) return;
    const orderA = a.data.def.order;
    const orderB = b.data.def.order === orderA ? orderA + delta : b.data.def.order;
    void save([
      { docType: 'subject', id: a.id, data: { def: { ...a.data.def, order: orderB } } },
      { docType: 'subject', id: b.id, data: { def: { ...b.data.def, order: orderA } } },
    ]);
  }

  const editing = creating ?? selected?.data.def ?? null;
  const editorKey = creating
    ? `new:${creating.id}`
    : `${selected?.id ?? ''}:${selected?.revision ?? -1}`;

  return (
    <div className="catalog-split">
      <div className="pane pane--list">
        <div className="toolbar">
          <h2 className="section-title">科目</h2>
          <CatalogSummary errors={summary.errors.length} warnings={summary.warnings.length} />
        </div>
        <table className="table" aria-label="科目一覧">
          <thead>
            <tr>
              <th>ID</th>
              <th>表示名</th>
              <th>状態</th>
              <th>キャラクター</th>
              <th>通貨</th>
              <th>順序</th>
              {editable && <th aria-label="並べ替え" />}
            </tr>
          </thead>
          <tbody>
            {drafts.subject.map((entry, index) => (
              <tr
                key={entry.id}
                className={entry.id === selectedId && !creating ? 'row--active' : undefined}
                onClick={() => {
                  setCreating(null);
                  setOutcome(null);
                  setSelectedId(entry.id);
                }}
              >
                <td className="mono">{entry.id}</td>
                <td>{entry.data.def.name.ja || <span className="muted">(未設定)</span>}</td>
                <td>{SUBJECT_STATUS_LABELS[entry.data.def.status] ?? entry.data.def.status}</td>
                <td className="mono">{entry.data.def.characterId}</td>
                <td className="mono">{entry.data.def.currency}</td>
                <td>{entry.data.def.order}</td>
                {editable && (
                  <td className="nowrap">
                    <button
                      className="button button--small"
                      type="button"
                      aria-label={`${entry.id} を上へ`}
                      disabled={busy || index === 0}
                      onClick={(event) => {
                        event.stopPropagation();
                        move(index, -1);
                      }}
                    >
                      ↑
                    </button>
                    <button
                      className="button button--small"
                      type="button"
                      aria-label={`${entry.id} を下へ`}
                      disabled={busy || index === drafts.subject.length - 1}
                      onClick={(event) => {
                        event.stopPropagation();
                        move(index, 1);
                      }}
                    >
                      ↓
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {editable && (
          <section className="card" aria-label="科目を追加">
            <h3>科目を追加</h3>
            {available.length === 0 ? (
              <p className="muted">追加できる通貨がありません(すべて科目になっています)。</p>
            ) : (
              <div className="toolbar">
                <select
                  className="select select--inline"
                  aria-label="新しい科目のID"
                  value={newId}
                  onChange={(event) => {
                    setNewId(event.target.value);
                  }}
                >
                  <option value="">ID(通貨)を選ぶ</option>
                  {available.map((id) => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
                </select>
                <button
                  className="button button--small"
                  type="button"
                  disabled={newId === ''}
                  onClick={() => {
                    const fallback =
                      characters.find((c) => c.id === 'legacy-avatar')?.id ??
                      characters[0]?.id ??
                      '';
                    setCreating(newSubjectDef(drafts, newId as SubjectDef['currency'], fallback));
                    setSelectedId(newId);
                    setOutcome(null);
                    setNewId('');
                  }}
                >
                  作成
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      <div className="pane">
        <SaveNotice outcome={outcome} onReload={() => void store.reload()} />
        {editing === null ? (
          <p className="muted">左の一覧から科目を選んでください。</p>
        ) : (
          <SubjectEditor
            key={editorKey}
            initial={editing}
            isNew={creating !== null}
            store={store}
            editable={editable}
            busy={busy}
            characters={characters}
            onSave={(def) => save([{ docType: 'subject', id: def.id, data: { def } }])}
            onCancel={() => {
              setCreating(null);
              setSelectedId(drafts.subject[0]?.id ?? null);
            }}
          />
        )}
      </div>
    </div>
  );
}

interface EditorProps {
  readonly initial: SubjectDef;
  readonly isNew: boolean;
  readonly store: CatalogStore;
  readonly editable: boolean;
  readonly busy: boolean;
  readonly characters: readonly CharacterDef[];
  readonly onSave: (def: SubjectDef) => Promise<boolean>;
  readonly onCancel: () => void;
}

function SubjectEditor({
  initial,
  isNew,
  store,
  editable,
  busy,
  characters,
  onSave,
  onCancel,
}: EditorProps) {
  const [working, setWorking] = useState<SubjectDef>(initial);
  const result = useMemo(
    () => validateDrafts(store.drafts, { subject: working }),
    [store.drafts, working],
  );
  const issues = issuesFor(result, 'subject', working.id);

  function update(patch: Partial<SubjectDef>): void {
    setWorking((current) => ({ ...current, ...patch }));
  }

  return (
    <form
      className="editor"
      aria-label="科目の編集"
      onSubmit={(event) => {
        event.preventDefault();
        void onSave(working);
      }}
    >
      <h3>
        {isNew ? '新しい科目' : '科目'} <span className="mono">{working.id}</span>
      </h3>
      <ReadOnlyNote editable={editable} />
      <IssueList issues={issues} />
      <fieldset className="editor__fields" disabled={!editable || busy}>
        <label className="field">
          <span className="field__label">ID(作成後は変更できません)</span>
          <input className="input mono" value={working.id} readOnly aria-readonly />
        </label>
        <label className="field">
          <span className="field__label">表示名(ja。ひらがな優先)</span>
          <input
            className="input"
            aria-label="表示名"
            value={working.name.ja ?? ''}
            onChange={(event) => {
              update({ name: { ...working.name, ja: event.target.value } });
            }}
          />
          <IssueList issues={fieldIssues(issues, 'name')} />
        </label>
        <label className="field">
          <span className="field__label">通貨(ID と同じ)</span>
          <input className="input mono" value={working.currency} readOnly aria-readonly />
        </label>
        <div className="field">
          <span className="field__label">色</span>
          <div className="toolbar">
            <input
              type="color"
              aria-label="色の選択"
              value={/^#[0-9a-fA-F]{6}$/.test(working.color) ? working.color : '#888888'}
              onChange={(event) => {
                update({ color: event.target.value });
              }}
            />
            <input
              className="input mono"
              aria-label="色"
              value={working.color}
              onChange={(event) => {
                update({ color: event.target.value });
              }}
            />
          </div>
          <IssueList issues={fieldIssues(issues, 'color')} />
        </div>
        <label className="field">
          <span className="field__label">アイコン(絵文字またはパス)</span>
          <input
            className="input"
            aria-label="アイコン"
            value={working.icon}
            onChange={(event) => {
              update({ icon: event.target.value });
            }}
          />
          <IssueList issues={fieldIssues(issues, 'icon')} />
        </label>
        <label className="field">
          <span className="field__label">キャラクター</span>
          <select
            className="select"
            aria-label="キャラクター"
            value={working.characterId}
            onChange={(event) => {
              update({ characterId: event.target.value });
            }}
          >
            {!characters.some((c) => c.id === working.characterId) && (
              <option value={working.characterId}>{working.characterId}(なし)</option>
            )}
            {characters.map((character) => (
              <option key={character.id} value={character.id}>
                {character.id}({character.name.ja}){character.status === 'retired' ? ' 廃止' : ''}
              </option>
            ))}
          </select>
          <IssueList issues={fieldIssues(issues, 'characterId')} />
        </label>
        <label className="field">
          <span className="field__label">状態</span>
          <select
            className="select"
            aria-label="状態"
            value={working.status}
            onChange={(event) => {
              update({ status: event.target.value as SubjectDef['status'] });
            }}
          >
            {SUBJECT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {SUBJECT_STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">並び順</span>
          <NumberInput
            label="並び順"
            value={working.order}
            onChange={(order) => {
              update({ order });
            }}
          />
          <IssueList issues={fieldIssues(issues, 'order')} />
        </label>
      </fieldset>
      {editable && (
        <div className="actions">
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? '保存中…' : '保存'}
          </button>
          {isNew && (
            <button className="button" type="button" onClick={onCancel}>
              取り消し
            </button>
          )}
        </div>
      )}
    </form>
  );
}
